import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft, HardDrive, CheckCircle2, Clock, Shield, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';

const LaptopHDDRepairPage = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Заміна HDD/SSD ноутбука в Одесі - CIMM | Апгрейд</title>
        <meta name="description" content="Заміна HDD на SSD, установка SSD в ноутбук. Перенесення даних, збільшення швидкості. Ціни від 300 грн + вартість SSD. ☎ +38 097 417 8788" />
      </Helmet>

      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Головна</Link>
            <Link to="/phones" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Телефони</Link>
            <Link to="/laptops" className="text-orange-500">Ноутбуки</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Контакти</Link>
          </nav>
          <a href="tel:+380974178788" className="neon-text font-semibold hover:drop-shadow-[0_0_15px_rgba(255,140,0,0.8)] transition-all duration-300">
            +38 097 417 8788
          </a>
        </div>
      </header>

      <section className="pt-32 pb-12 px-4 dark-section">
        <div className="container mx-auto max-w-6xl relative z-10">
          <Link to="/laptops" className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-500 mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Повернутися до всіх послуг
          </Link>

          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center">
                <HardDrive className="w-10 h-10 text-orange-500 icon-glow" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Заміна <span className="neon-text">HDD на SSD</span>
            </h1>
            <p className="text-xl text-gray-400">Підвищимо швидкість ноутбука в 5-10 разів</p>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 dark-section">
        <div className="container mx-auto max-w-6xl relative z-10">
          
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="glass-card p-6 text-center">
              <Zap className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">В 5-10 разів</h3>
              <p className="text-gray-400 text-sm">Швидше</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Clock className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">1-2 години</h3>
              <p className="text-gray-400 text-sm">Установка</p>
            </div>
            <div className="glass-card p-6 text-center">
              <CheckCircle2 className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">Всі дані</h3>
              <p className="text-gray-400 text-sm">Зберігаємо</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Shield className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">Гарантія</h3>
              <p className="text-gray-400 text-sm">На роботу</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Переваги SSD</h2>
              <ul className="space-y-4">
                {[
                  'Швидкість роботи зростає в 5-10 разів',
                  'Програми відкриваються миттєво',
                  'Windows завантажується за 10 секунд',
                  'Тихіша робота (немає шуму)',
                  'Менше енергоспоживання',
                  'Стійкість до ударів і вібрацій'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              
              {/* Image */}
              <div className="mt-6 rounded-lg overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/10558598/pexels-photo-10558598.jpeg" 
                  alt="Апгрейд ноутбука"
                  className="w-full h-48 object-cover"
                />
                <p className="text-xs text-gray-500 mt-2 text-center">Процес установки SSD</p>
              </div>
            </div>

            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Що ми робимо?</h2>
              <div className="space-y-4">
                {[
                  { step: '1', text: 'Консультація', desc: 'Підбираємо SSD під ваш ноутбук' },
                  { step: '2', text: 'Клонування', desc: 'Переносимо всі дані зі старого HDD' },
                  { step: '3', text: 'Установка SSD', desc: 'Встановлюємо новий диск' },
                  { step: '4', text: 'Оптимізація', desc: 'Налаштовуємо систему' },
                  { step: '5', text: 'Тестування', desc: 'Перевіряємо швидкість' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-orange-500 font-bold">{item.step}</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold">{item.text}</p>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Video Placeholder */}
              <div className="mt-6 bg-gray-900/50 rounded-lg p-6 border border-orange-500/20 text-center">
                <p className="text-gray-400 text-sm mb-2">📹 Апгрейд на SSD</p>
                <p className="text-xs text-gray-500">Наші роботи в <a href="https://www.instagram.com/cimmodua/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400">Instagram</a></p>
              </div>
            </div>
          </div>

          {/* Detailed Process */}
          <div className="glass-card p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Як відбувається апгрейд на SSD</h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HardDrive className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">Підбір SSD під ваш ноутбук</h3>
                  <p className="text-gray-400">Спочатку ми визначаємо який інтерфейс підтримує ваш ноутбук: SATA (2.5"), M.2 SATA, або M.2 NVMe. Також враховуємо ваші потреби: для офісної роботи достатньо 256GB, для роботи з фото/відео краще 512GB або 1TB.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">Клонування системи</h3>
                  <p className="text-gray-400">За допомогою спеціального програмного забезпечення ми переносимо всю операційну систему, програми та файли зі старого HDD на новий SSD. Процес займає від 30 хвилин до 2 годин залежно від обсягу даних. Всі ваші налаштування та файли залишаються на місці!</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">Установка та оптимізація</h3>
                  <p className="text-gray-400">Встановлюємо SSD в ноутбук, оптимізуємо налаштування Windows для роботи з SSD (вимикаємо дефрагментацію, увімкаємо TRIM). Тестуємо швидкість читання/запису. Після апгрейду ваш ноутбук працює як новий - швидше завантажується, програми відкриваються миттєво!</p>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Вартість послуги</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gray-900/30 rounded-xl p-6 border border-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-2">SSD 256GB</h3>
                <p className="text-gray-400 text-sm mb-4">Kingston, WD, Samsung</p>
                <p className="text-3xl font-bold text-orange-500">від 1200 грн</p>
                <p className="text-gray-400 text-xs mt-2">+ установка 300 грн</p>
              </div>
              <div className="bg-gray-900/30 rounded-xl p-6 border border-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-2">SSD 512GB</h3>
                <p className="text-gray-400 text-sm mb-4">Kingston, WD, Samsung</p>
                <p className="text-3xl font-bold text-orange-500">від 1800 грн</p>
                <p className="text-gray-400 text-xs mt-2">+ установка 300 грн</p>
              </div>
              <div className="bg-gray-900/30 rounded-xl p-6 border border-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-2">SSD 1TB</h3>
                <p className="text-gray-400 text-sm mb-4">Kingston, WD, Samsung</p>
                <p className="text-3xl font-bold text-orange-500">від 2800 грн</p>
                <p className="text-gray-400 text-xs mt-2">+ установка 300 грн</p>
              </div>
            </div>
            <p className="text-center text-gray-400 text-sm mt-6">
              * Ціни на SSD актуальні. Можливо встановити ваш SSD.
            </p>
          </div>

          <div className="cta-card p-8 text-center cta-border">
            <h2 className="text-3xl font-bold text-white mb-4">Хочете прискорити ноутбук?</h2>
            <p className="text-gray-400 mb-6">Заміна HDD на SSD - найкраще рішення!</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:+380974178788">
                <Button className="futuristic-btn bg-orange-500 hover:bg-orange-600 text-white border-0" size="lg">
                  Зателефонувати зараз
                </Button>
              </a>
              <Link to="/contacts">
                <Button variant="outline" className="neon-border border-orange-500 text-orange-500 hover:bg-orange-500/10 bg-transparent" size="lg">
                  Де ми знаходимось
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </section>

      <footer className="cyber-footer py-12 px-4">
        <div className="container mx-auto">
          <div className="text-center text-gray-400">
            <p>© 2026 CIMM Computers. Всі права захищені.</p>
            <p className="text-xs text-gray-500 mt-3 max-w-3xl mx-auto">Незалежний сервісний центр з постгарантійного обслуговування. Ми не є офіційним (авторизованим) сервісним центром і не пов'язані з виробниками техніки (Apple, Samsung, Sony, Xiaomi та ін.). Усі назви брендів належать їхнім власникам.</p>
            <p className="mt-2">
              <Link to="/privacy-policy" className="hover:text-orange-500 transition-colors">Політика конфіденційності</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LaptopHDDRepairPage;